import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { authApi, subApi } from './api';

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState(null); // active tenant scope

  const applyMe = useCallback((me) => {
    setUser(me);
    setTenant(me.tenant || null);
    setTenantId(me.tenant_id || null);
  }, []);

  const refreshSubscription = useCallback(async (role, tid) => {
    if (role === 'super_admin') { setSubscription(null); return null; }
    if (!tid) { setSubscription(null); return null; }
    try {
      const s = await subApi.get();
      setSubscription(s);
      return s;
    } catch { return null; }
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      const me = await authApi.me();
      applyMe(me);
      await refreshSubscription(me.role, me.tenant_id);
    } catch {
      setUser(null);
      setTenant(null);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [applyMe, refreshSubscription]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const refresh = useCallback(async () => {
    const me = await authApi.me();
    applyMe(me);
    await refreshSubscription(me.role, me.tenant_id);
    return me;
  }, [applyMe, refreshSubscription]);

  const login = useCallback(async (email, password) => {
    await authApi.login(email, password);
    return refresh();
  }, [refresh]);

  const register = useCallback(async (body) => {
    await authApi.register(body);
    return refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch (e) { console.error('Logout request failed:', e); }
    setUser(null);
    setTenant(null);
    setSubscription(null);
    setTenantId(null);
  }, []);

  const features = useMemo(() => new Set(subscription?.features || []), [subscription]);
  const hasFeature = useCallback(
    (f) => (user?.role === 'super_admin' ? true : features.has(f)),
    [user, features],
  );

  const value = useMemo(() => ({
    user,
    tenant,
    subscription,
    loading,
    login,
    register,
    logout,
    refresh,
    refreshSubscription,
    hasFeature,
    isSuper: user?.role === 'super_admin',
    tenantId,
    setTenantId,
  }), [user, tenant, subscription, loading, login, register, logout, refresh, refreshSubscription, hasFeature, tenantId]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
